<script setup>
import moment from 'moment'
import { SurveyType } from '@/enums/survey/survey.type'
import { useRoute, useRouter } from 'vue-router'
import { cookieRef } from '@/@layouts/stores/config'
import TicketTable from '@/views/survey/ticket-management/TicketTable.vue'
import SubscriptionTable from '@/views/survey/SubscriptionTable.vue'
import EpisodeDrawer from '@/views/common/EpisodeDrawer.vue'

definePage({
  meta: {
    name: "Survey Subscriptions",
  },
})

const route = useRoute()
const router = useRouter()

const unitId = computed(() => cookieRef('selectedUnit').value)

const surveyType = computed(() => route.params.type)
const surveyId = computed(() => route.params.survey ?? null)

const survey = ref()

// Fetch survey data first
if (surveyId.value) {
  await useApi(createUrl(`/survey/surveys/${surveyId.value}`, {
    query: {
      with: ['schedules'],
    },
  }), {
    beforeFetch: () => {},
    afterFetch: ({ data }) => {
      survey.value = data
    },
    onFetchError: error => {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    },
  })
}

// Tab and table state (after survey is loaded)
const ALL_SCHEDULES = 'all'

const activeTab = ref(ALL_SCHEDULES)
const searchQuery = ref('')
const sortBy = ref()
const orderBy = ref()
const refSubscriptionTable = ref(null)

// Collection status filter (All, Collected, Not Collected)
const collectionStatus = ref('all')

// Specialties filter
const selectedSpecialty = ref(null)

const { data: specialties } = useApi(createUrl('/masters/episode/specialties'), {
  beforeFetch: ({ cancel }) => {
    if (surveyType.value !== SurveyType.CUSTOMER) cancel()
  },
})

// Default date range - Last 30 days
const defaultFromDate = moment().subtract(30, 'days').format('YYYY-MM-DD')
const defaultToDate = moment().format('YYYY-MM-DD')

// Date range state
const scheduleDateRange = ref(`${defaultFromDate} to ${defaultToDate}`)
const fromDate = ref(defaultFromDate)
const toDate = ref(defaultToDate)

// Date Range Options
const dateRangeOptions = ref({
  mode: 'range',
  maxDate: null,
  dateFormat: 'Y-m-d',
  enableTime: false,
  defaultDate: [defaultFromDate, defaultToDate],
  onChange: (selectedDates, dateStr) => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : fromDate.value
    }
  },
})

// Export loading state
const exportLoading = ref(false)

// Get active schedule ID. `null` on the All tab, which drops the
// survey_schedule_id filter so the export groups its rows by customer.
const activeScheduleId = computed(() => {
  if (activeTab.value === ALL_SCHEDULES) return null

  return activeTab.value
})

// Computed filters for subscription API
const computedFilters = computed(() => {
  const filters = {
    unit_id: unitId.value,
    survey_schedule_id: activeScheduleId.value,
    schedule_at: [fromDate.value, toDate.value],
  }

  if (surveyType.value === SurveyType.CUSTOMER && selectedSpecialty.value) {
    filters['episode.specialty'] = selectedSpecialty.value
  }

  // Add collected_at filter based on collection status
  if (collectionStatus.value === 'collected') {
    filters.collected_at = 'not-null'
  } else if (collectionStatus.value === 'notCollected') {
    filters.collected_at = 'null'
  } else if (collectionStatus.value === 'terminated') {
    filters.terminated_at = 'not-null'
  }

  return filters
})

// Refresh subscriptions
const refreshSubscriptions = () => {
  refSubscriptionTable.value?.fetchSubscriptions()
}

// Export subscriptions
const exportSubscriptions = async () => {
  exportLoading.value = true

  const url = createUrl('survey/survey-responses/export', {
    query: {
      q: searchQuery,
      search: {
        ...computedFilters.value,
        survey_id: surveyId.value,
      },
      sort: sortBy,
      order_by: orderBy,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(error => {})

  exportLoading.value = false
}

// Episode Drawer
const isEpisodeDrawerOpen = ref(false)
const selectedEpisodeId = ref(null)

const onClickViewEpisode = item => {
  selectedEpisodeId.value = item.episode_id
  isEpisodeDrawerOpen.value = true
}

const ticketTableFilter = ref(null)
const isTicketTableDialogVisible = ref(false)

const onClickViewTickets = item => {
  ticketTableFilter.value = {
    survey_schedule_subscription_id: item.id,
  }
  isTicketTableDialogVisible.value = true
}

watch(
  () => isTicketTableDialogVisible.value,
  value => {
    if (value === false) ticketTableFilter.value = null
  },
)
</script>

<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center gap-4 mb-6">
      <!--
        <VBtn
        icon
        variant="text"
        @click="goBack"
        >
        <VIcon icon="tabler-arrow-left" />
        </VBtn> 
      -->
      <div>
        <h4 class="text-h4">
          {{ survey?.title ?? 'Survey' }}
        </h4>
        <p class="text-body-2 text-disabled mb-0">
          Manage subscriptions for each schedule
        </p>
      </div>
    </div>

    <!-- No Schedules Alert -->
    <VAlert
      v-if="!survey?.schedules?.length"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      No schedules found for this survey. Please create schedules first.
    </VAlert>

    <!-- Tabs and Table -->
    <VCard
      v-else
      variant="flat"
      class="border"
    >
      <!-- Schedule Tabs -->
      <VTabs
        v-model="activeTab"
        class="mt-1"
        bg-color="transparent"
      >
        <VTab :value="ALL_SCHEDULES">
          All
        </VTab>
        <VTab
          v-for="schedule in survey.schedules"
          :key="schedule.id"
          :value="schedule.id"
        >
          {{ schedule.name }}
        </VTab>
      </VTabs>

      <!-- <VDivider /> -->

      <!-- Search and Filters -->
      <VCardText class="d-flex flex-column gap-4">
        <!-- Row 1: Search, Specialty and Date Picker -->
        <div class="d-flex flex-wrap align-center gap-4">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search by name, phone number, or ID..."
            density="compact"
            clearable
            class="flex-grow-1"
            style="min-inline-size: 300px;"
          />
          <AppSelect
            v-if="surveyType === SurveyType.CUSTOMER"
            v-model="selectedSpecialty"
            placeholder="Select Specialty"
            :items="specialties ?? []"
            :item-title="item => item.specialty"
            :item-value="item => item.specialty"
            clearable
            style="min-inline-size: 200px;"
          />
          <AppDateTimePicker
            v-model="scheduleDateRange"
            placeholder="Select Date Range"
            :config="dateRangeOptions"
            style="min-inline-size: 250px;"
          />
        </div>

        <!-- Row 2: Status Toggle and Actions -->
        <div class="d-flex flex-wrap align-center gap-4">
          <VBtnToggle
            v-model="collectionStatus"
            color="primary"
            variant="outlined"
            density="comfortable"
            divided
          >
            <VBtn value="all">
              All
            </VBtn>
            <VBtn value="collected">
              Collected
            </VBtn>
            <VBtn value="notCollected">
              Not Collected
            </VBtn>
            <VBtn value="terminated">
              Terminated
            </VBtn>
          </VBtnToggle>

          <VSpacer />

          <VBtn
            variant="tonal"
            color="primary"
            prepend-icon="tabler-download"
            :loading="exportLoading"
            @click="exportSubscriptions"
          >
            Export Data
          </VBtn>

          <VBtn
            color="secondary"
            variant="tonal"
            prepend-icon="tabler-refresh"
            @click="refreshSubscriptions"
          >
            Refresh
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- Subscription Table -->
      <SubscriptionTable
        ref="refSubscriptionTable"
        :show-schedule="activeScheduleId === null"
        :filters="computedFilters"
        :search-query="searchQuery"
        @on-click-view-tickets="onClickViewTickets"
        @on-click-view-episode="onClickViewEpisode"
      />
    </VCard>
  </div>

  <!-- Episode Drawer -->
  <EpisodeDrawer
    v-model:is-drawer-open="isEpisodeDrawerOpen"
    :episode-id="selectedEpisodeId"
  />

  <!-- Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <!-- 👉 dialog close btn -->
    <DialogCloseBtn
      size="small"
      @click="() => (isTicketTableDialogVisible = !isTicketTableDialogVisible)"
    />

    <VCard>
      <TicketTable
        ref="refTicketTable"
        :ticket-filters="ticketTableFilter"
        :action-visible="false"
        :style="{
          cardVariant: 'flat',
        }"
      />
    </VCard>
  </VDialog>
</template>
